import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Eye, Archive, Search, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { orders as ordersApi } from '@/utils/api'
import { formatDateTime, getStatusLabel } from '@/utils/formatters'
import { useOrders } from '@/hooks/useOrders'
import { useProjects } from '@/hooks/useProjects'
import { useAuth } from '@/context/AuthContext'

interface OrderRow { id: number; submitted_at?: string; created_at: string; project_name?: string; bauleiter_name?: string; items_count: number; status: string }

const statusVariant = (s: string) => s === 'submitted' ? 'warning' as const : s === 'ordered' ? 'success' as const : s === 'in_progress' ? 'info' as const : 'secondary' as const

export default function Bestellungen() {
  const [searchParams] = useSearchParams()
  const [filters, setFilters] = useState({ status: searchParams.get('status') || 'submitted', projectId: '', search: '' })
  const [deleteTarget, setDeleteTarget] = useState<OrderRow | null>(null)
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()

  const apiParams: Record<string, string> = {}
  if (filters.status !== 'all') apiParams.status = filters.status
  if (filters.projectId) apiParams.projectId = filters.projectId
  if (filters.search) apiParams.search = filters.search

  const { data: ordersData, isLoading: loading } = useOrders(apiParams)
  const orders: OrderRow[] = (ordersData as OrderRow[] | undefined) ?? []

  const { data: projectsData } = useProjects()
  const projects: { id: number; name: string }[] = (projectsData as { id: number; name: string }[] | undefined) ?? []

  const archiveMutation = useMutation({
    mutationFn: (id: number) => ordersApi.archive(id),
    onSuccess: () => {
      toast.success('Archiviert')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (e) => { toast.error(e instanceof Error ? e.message : 'Fehler') },
  })

  const handleArchive = (id: number) => { archiveMutation.mutate(id) }

  const deleteMutation = useMutation({
    mutationFn: (id: number) => ordersApi.remove(id),
    onSuccess: () => {
      toast.success('Bestellung gelöscht')
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (e) => { toast.error(e instanceof Error ? e.message : 'Fehler beim Löschen') },
  })

  const columns: ColumnDef<OrderRow>[] = [
    {
      accessorKey: 'submitted_at',
      header: 'Datum',
      cell: ({ row }) => formatDateTime(row.original.submitted_at || row.original.created_at),
    },
    {
      accessorKey: 'project_name',
      header: 'Bauvorhaben',
      cell: ({ row }) => <span className="font-medium">{row.original.project_name || '-'}</span>,
    },
    {
      accessorKey: 'bauleiter_name',
      header: 'Bauleiter',
      cell: ({ row }) => row.original.bauleiter_name || '-',
    },
    {
      accessorKey: 'items_count',
      header: 'Positionen',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <Badge variant={statusVariant(row.original.status)}>{getStatusLabel(row.original.status)}</Badge>,
    },
    {
      id: 'actions',
      header: 'Aktionen',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Link to={`/admin/bestellungen/${row.original.id}`}><Button size="sm" onClick={(e) => e.stopPropagation()}><Eye className="h-4 w-4" /> Oeffnen</Button></Link>
          {row.original.status === 'ordered' && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleArchive(row.original.id) }}>
              <Archive className="h-4 w-4" /> Archivieren
            </Button>
          )}
          {isAdmin && (
            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original) }}>
              <Trash2 className="h-4 w-4" /> Löschen
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title="Bestellungen" description={`${orders.filter(o => o.status === 'submitted').length} warten auf Bearbeitung`} />

      <Card className="mb-6"><CardContent className="flex flex-wrap gap-4 py-4">
        <div className="relative min-w-[200px] max-w-sm flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Suche..." value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} className="pl-10" /></div>
        <select value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm">
          <option value="submitted">Neu eingereicht</option><option value="in_progress">In Bearbeitung</option><option value="ordered">Bestellt</option><option value="archived">Archiviert</option><option value="draft">Entwuerfe</option><option value="all">Alle</option>
        </select>
        <select value={filters.projectId} onChange={(e) => setFilters({...filters, projectId: e.target.value})} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm">
          <option value="">Alle Bauvorhaben</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </CardContent></Card>

      {loading ? <div className="space-y-2">{Array.from({length:4}).map((_,i) => <Skeleton key={i} className="h-12 w-full" />)}</div> : (
        <DataTable columns={columns} data={orders} emptyText="Keine Bestellungen" />
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bestellung löschen?</DialogTitle>
            <DialogDescription>
              Die Bestellung{deleteTarget?.project_name ? ` „${deleteTarget.project_name}"` : ''} wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>
              <Trash2 className="h-4 w-4" /> Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
